import { useEffect, useState } from 'react';
import { etiquetaIdioma, resolverIdioma } from '../lib/idioma';
import { ClaimTypeChip } from '../components/ClaimTypeChip';
import { ESPERA_ETIQUETAS } from '../lib/tipoDeEspera';
import { Link, useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { AlarmClock, AlertTriangle, Building2, CalendarDays, Camera, Check, ChevronLeft, ChevronRight, Clock4, FilePenLine, FileText, HardHat, Home, Inbox, Landmark, Languages, LayoutGrid, List, Mail, MapPin, PenLine, Phone, Plus, Search, Timer, User, Users, X } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { FollowUpPanel } from '../components/FollowUpPanel';
import { formatUsd, localeUI } from '../lib/format';
import { statusColor } from '../lib/status';
import { NeedsSupplementChip } from './QueuePage';
import { followUpState, followUpLabel, FOLLOWUP_COLOR } from '../lib/followUpColor';
import { ClaimDrawer } from '../components/ClaimDrawer';
import { ScheduleModal } from '../components/ScheduleModal';
import { RequestVisitButtons } from '../components/RequestVisitButtons';
import { SCHED_STATUS_TO_APPT_TYPE } from '../lib/visitType';
import { AddLeadModal } from '../components/AddLeadModal';
import { IntakeRowDetail } from '../components/IntakeRowDetail';
import { useScrollOnOpen } from '../lib/useScrollOnOpen';
import { usePersistentState } from '../lib/usePersistentState';
import { CalendarView } from '../components/CalendarView';
import { WelcomeCallSection } from '../components/WelcomeCallSection';
import { ReadyToFileSection } from '../components/ReadyToFileSection';
import { ClaimsWaitingApprovalSection } from '../components/ClaimsWaitingApprovalSection';
import { ClaimsAtrSection } from '../components/ClaimsAtrSection';
import { ClaimsGenericSection } from '../components/ClaimsGenericSection';
import { CocQueueSection } from '../components/CocQueueSection';
import { ViewSettingsButton, ViewSettingsModal } from '../components/ViewSettingsModal';
import { ToSignSection } from '../components/ToSignSection';
import { ClaimInfoHeader, type FlowRow } from '../components/AcceptedFlowShared';
import { InsuranceLink } from '../components/InsuranceCard';
import { ContractorLink } from '../components/ContractorCard';
import { WorkOnItButton } from '../components/WorkOnItButton';
import { SecondOpinionButton } from '../components/SecondOpinionButton';
import { SecondOpinionSetting } from '../components/SecondOpinionSetting';
import { BulkProvider, useBulkSelect, BulkRowCheck } from '../lib/bulkSelect';
import { ClaimBulkBar } from '../components/ClaimBulkBar';
import { RChip } from '../components/ui/RChip';
import { MoneyChip } from '../components/ui/MoneyChip';
import { diasHabilesDesde } from '../lib/diasHabiles';

/**
 * Vistas genéricas del framework multi-departamento. Misma estructura para todos
 * los departamentos (Claims/Scheduling/Intake): toggle Cards/Lines, filas/tarjetas
 * expandibles, y click en el NOMBRE del cliente abre la tarjeta lateral (foto+info).
 * Payments y Supplements conservan su UI propia y más rica.
 */

type Status = { key: string; label: string; sort_order: number; is_terminal: boolean; color: string | null };
type QueueRow = {
  presence_id: string;
  status: string | null;
  status_since: string;
  /** Marcado "necesita suplemento" (supplement_review_flags): desde cuándo. Ver NeedsSupplementChip. */
  review_flag_since?: string | null;
  next_follow_up_at?: string | null;
  /** El próximo EFECTIVO (agendado o calculado) — ver backend proximoFollowUp. */
  next_follow_up_calc?: string | null;
  last_follow_up_at?: string | null;
  last_follow_up_by?: string | null;
  last_follow_up_by_id?: string | null;
  claim_id: string;
  claim_number: string | null;
  homeowner_name: string | null;
  contractor_name: string | null;
  /** FK del contratista: ContractorLink abre la ficha EXACTA (por nombre puede abrir un duplicado). */
  contractor_id?: string | null;
  insurance_company: string | null;
  /** Account manager real (claims.account_manager) — assigned_person es el encargado del board de Payments. */
  account_manager?: string | null;
  assigned_person: string | null;
  assigned_team_name: string | null;
  /** Personas del team asignado: el chip decía el equipo, no quién lo tiene. */
  assigned_team_people?: string | null;
  /** La persona del PUESTO del depto de la vista — lo que se MUESTRA. */
  assigned_position_person?: string | null;
  rcv_amount_cents: number | null;
  address: string | null;
  photo_url: string | null;
  is_existing_claim?: boolean;
  second_opinion_at?: string | null;
  second_opinion_reason?: string | null;
  claim_type?: string | null;
  type_of_loss?: string | null;
  /** 'YYYY-MM-DD' — viene por to_char, NUNCA como Date (ver fmtDol). */
  date_of_loss?: string | null;
  claim_type_by?: string | null;
  claim_type_at?: string | null;
  preferred_language?: string | null;
  has_photos?: boolean;
  has_policy?: boolean;
  // Enriquecidos solo para Intake:
  intake_status?: 'complete' | 'incomplete' | 'missing';
  has_intake_submission?: boolean;
  welcome_call_done?: boolean;
  submission_token?: string | null;
  photos_url?: string | null;
  contract_sign_method?: string | null;
  contract_signed?: boolean;
  has_contract?: boolean;
  phone?: string | null;
  homeowner_email?: string | null;
  lifecycle_status?: string | null;
  /** Qué visita falta, derivada de la evidencia (adjuster_meeting | reinspection | welcome_call | atr). */
  wait_kind?: string | null;
  wc_result?: string | null;
  wc_problem?: boolean;
  wc_has_recording?: boolean;
  sched_type?: string;
  scheduled_start?: string | null;
  scheduled_inspector?: string | null;
  assigned_team_id?: string | null;
};

// DÍAS HÁBILES (dueño 2026-10-14): los días de las colas y tarjetas del dashboard van de lunes a viernes.
const daysSince = (iso: string | null): number | null => diasHabilesDesde(iso);

// Tipos canónicos de agendamiento (idénticos a ClaimRoute shared/tasks.ts TASK_TYPES): se muestran
// los 4 siempre. adjuster_meeting E inspection son LO MISMO ("Inspection", cubre inspección y re-inspección).
const SCHED_TYPES = ['inspection', 'atr', 'photos', 'check_pickup', 'material_sample'] as const;
/** Mapea el tipo crudo de la cita al tipo canónico del filtro (inspection agrupa adjuster_meeting/etc). */
const canonSchedType = (t: string | undefined): string =>
  t === 'atr' || t === 'photos' || t === 'check_pickup' || t === 'material_sample' ? t : 'inspection';

export function DepartmentHome() {
  const { key } = useParams();
  const { t } = useI18n();
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get<{ statuses: Status[] }>(`/api/departments/${key}/statuses`)
      .then((r) => { if (alive) setStatuses(r.statuses); })
      .catch(() => { if (alive) setStatuses([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [key]);

  if (loading) return <div className="page-empty">{t('common.loadingDots')}</div>;

  return (
    <div>
      {/* Buzón del departamento: todos sus emails (enviados + recibidos), buscables. */}
      <div style={{ marginBottom: 14 }}>
        <Link to={`/dept/${key}/inbox`} className="btn ghost sm"><Inbox size={14} strokeWidth={1.75} /> {t('inbox.deptInbox')}</Link>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 12 }}>
        {statuses.map((s) => (
          <Link
            key={s.key}
            to={`/dept/${key}/queue/${encodeURIComponent(s.key)}`}
            style={{
              display: 'block', padding: 16, borderRadius: 14,
              border: '1px solid var(--line)', background: 'var(--card)',
              textDecoration: 'none', color: 'var(--ink)', fontWeight: 600,
            }}
          >
            {s.label}
          </Link>
        ))}
        {statuses.length === 0 && <div className="page-empty">{t('dept.noStatuses')}</div>}
      </div>
    </div>
  );
}

/** Etiqueta "Existing" — un claim ya abierto que entramos a trabajar (no needs filing). */
function ExistingBadge() {
  const { t } = useI18n();
  return <RChip variant="info" title={t('ird.existingHint')}>{t('ird.existing')}</RChip>;
}

const daysAt = (iso: string | null): number | null => diasHabilesDesde(iso);

/** Badges de la fila de Intake según la etapa (fotos, intake questions, welcome call, firma, checks).
 *  Chips de readiness → RChip (mockup B-working-views: fila del Claim Review con rchips). */
function IntakeBadges({ r }: { r: QueueRow }) {
  const { t } = useI18n();
  const st = r.status || '';
  const badges: React.ReactNode[] = [];

  // Fotos (todas las etapas): abrir si hay, si no "waiting".
  badges.push(r.has_photos && r.photos_url
    ? <a key="ph" href={r.photos_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="rchip ok" style={{ textDecoration: 'none' }} title={t('photos.open')}><Camera size={11} strokeWidth={2} /> {t('iq.photosUp')}</a>
    : <RChip key="ph" variant="warn" icon={Camera}>{t('iq.photosWait')}</RChip>);

  // Lead: account manager (verde si está / ámbar "add" si falta) + idioma + tipo de claim.
  if (st === 'Lead') {
    // Account-manager-primero (mismo criterio que el resto de la app): la columna propia
    // `account_manager` manda; assigned_person (board de Payments) y el equipo son respaldos.
    const am = r.account_manager || r.assigned_person || r.assigned_team_name;
    badges.push(am
      ? <RChip key="am" variant="ok" icon={User} title={t('ct.accountManager')}>{am}</RChip>
      : <RChip key="am" variant="warn" icon={User} title={t('ct.accountManager')}>+ {t('ct.accountManager')}</RChip>);
    if (r.preferred_language) badges.push(<RChip key="lang" variant={resolverIdioma(r.preferred_language).code === 'spanish' ? 'warn' : 'info'} icon={Languages}>{etiquetaIdioma(r.preferred_language)}</RChip>);
    if (r.claim_type) badges.push(<RChip key="ct">{r.claim_type}</RChip>);
  }

  // Intake questions (Contract Sent en adelante).
  if (st !== 'Lead' && r.intake_status) {
    const map = { complete: ['ok', t('iq.qComplete')], incomplete: ['warn', t('iq.qIncomplete')], missing: ['no', t('iq.qMissing')] } as const;
    const [variant, label] = map[r.intake_status];
    badges.push(<RChip key="iq" variant={variant} icon={FilePenLine} title={t('iq.intakeQuestions')}>{label}</RChip>);
  }

  // Welcome call (Contract Sent en adelante).
  if (st !== 'Lead') {
    badges.push(<RChip key="wc" variant={r.welcome_call_done ? 'ok' : 'neutral'} icon={Phone}>{r.welcome_call_done ? t('iq.wcDone') : t('iq.wcPending')}</RChip>);
  }

  // Estado del contrato — SIEMPRE visible (pedido del equipo: en Monday veían el LOR,
  // aquí el chip dice cómo está el contrato en cada etapa).
  badges.push(r.contract_signed
    ? (r.contract_sign_method === 'paper'
      ? <RChip key="ctr" icon={FileText}>{t('iq.paperContract')}</RChip>
      : <RChip key="ctr" variant="ok" icon={PenLine}>{t('iq.eSigned')}</RChip>)
    : r.has_contract
      ? <RChip key="ctr" variant="warn" icon={Mail}>{t('iq.contractSent')}</RChip>
      : <RChip key="ctr" variant="no" icon={AlertTriangle}>{t('iq.noContract')}</RChip>);

  // Claim Review: checks verdes (contrato, póliza). Fotos ya se muestra arriba (con link), no lo repetimos.
  if (st === 'Claim Review') {
    const chk = (ok: boolean, label: string) => <RChip key={label} variant={ok ? 'ok' : 'no'} icon={ok ? Check : X}>{label}</RChip>;
    badges.push(chk(r.contract_signed ?? false, t('iq.contract')), chk(r.has_policy ?? false, t('iq.policy')));
  }

  // Días en la etapa.
  const d = daysAt(r.status_since);
  if (d != null) badges.push(<RChip key="d" variant={d >= 7 ? 'no' : 'info'} icon={Timer}>{d}d</RChip>);

  return <span style={{ display: 'inline-flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>{badges}</span>;
}

/** Cabecera del cliente: nombre → tarjeta lateral; claim# → abrir el claim. */
function RowHead({ r, onClient, onChanged }: { r: QueueRow; onClient: (id: string) => void; onChanged?: () => void }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
      {/* Selección múltiple. Sin BulkProvider no se pinta, así que las vistas que todavía no la
          habilitan quedan exactamente como estaban. */}
      <BulkRowCheck id={r.claim_id} />
      <button type="button" onClick={(e) => { e.stopPropagation(); onClient(r.claim_id); }}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--ink)', font: 'inherit', fontWeight: 600 }}>
        {r.homeowner_name || r.claim_number || `#${r.claim_id}`}
      </button>
      {/* EL TIPO DE CLAIM, EN TODAS LAS COLAS (pedido del dueño: "en first estimate y en todo
          supplementing y basicamente en todos lados quiero q salga el chip"). Vivía solo en el
          drawer, la tarjeta y el board de Claims — justo no en las pantallas donde se trabaja.
          El chip nunca sale vacío y se cambia con un clic desde acá mismo. */}
      <ClaimTypeChip claimId={r.claim_id} claim={r} size="sm" onSaved={onChanged} />
      {r.is_existing_claim && <ExistingBadge />}
      {/* Claim # en Geist Mono (principio 5 del rediseño: IDs siempre en mono). */}
      {r.claim_number && (
        <Link to={`/claims/${r.claim_id}`} onClick={(e) => e.stopPropagation()} className="badge gray" style={{ fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-mono)', textDecoration: 'none' }}>{r.claim_number}</Link>
      )}
    </span>
  );
}

const fmtDay = (iso: string | null): string => (iso ? new Date(iso).toLocaleDateString(localeUI()) : '—');
/**
 * DATE OF LOSS — MM/DD/YYYY, y con el mediodía puesto a mano.
 *
 * `date_of_loss` es una columna `date` que viaja como 'YYYY-MM-DD'. `new Date('2026-06-18')` la
 * parsea como MEDIANOCHE UTC, así que en cualquier huso al oeste (el nuestro) se muestra el DÍA
 * ANTERIOR — la fecha del siniestro corrida un día en la tarjeta que la oficina usa para trabajar.
 * Fijarla a las 12:00Z la deja fuera del alcance de cualquier corrimiento de huso.
 */
const fmtDol = (iso: string | null | undefined): string =>
  (iso ? new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString(localeUI()) : '—');

/** Fila/tarjeta expandible de un claim en la cola de un depto. */
function QueueItem({ r, view, onClient, neutral, deptKey, onChanged }: { r: QueueRow; view: 'cards' | 'lines'; onClient: (id: string) => void; neutral?: boolean; deptKey?: string; onChanged?: () => void }) {
  const { t } = useI18n();
  // Expansión PERSISTIDA por fila → al volver del claim completo con "atrás", la fila que
  // estabas trabajando sigue desplegada (queja masiva del equipo).
  const [open, setOpen] = usePersistentState<boolean>(`qopen:${r.presence_id}`, false);
  const rowRef = useScrollOnOpen<HTMLDivElement>(open);
  const days = daysSince(r.status_since);
  // En Claims el asignado es el EQUIPO de Claims (no el encargado de Payments). El
  // encargado de Payments se ve en la tarjeta lateral (drawer) al hacer click en el nombre.
  const assignee = (deptKey === 'claims' ? r.assigned_team_name : (r.assigned_person || r.assigned_team_name)) || '—';
  // `neutral` (colas de supervisión como Claims → Approved): NO follow-up. En vez del
  // badge de urgencia (rojo) mostramos el estatus real del claim + la fecha en que
  // entró (la vista Approved agrupa varios estatus: Approved, Waiting /Completion…).
  // Next follow-up de ESTE menú (cada tarea del sidebar lleva su propio reloj) — visible sin desplegar.
  // HOLD = pausado a propósito → sin chips/alertas de follow-up (pedido del equipo).
  const onHold = r.status === 'HOLD';
  const fuState = followUpState(onHold ? null : r.next_follow_up_at);
  const fuLabel = onHold ? null : followUpLabel(r.next_follow_up_at);
  // Días SIN follow-up: desde el último (o desde que llegó al menú si nunca hubo ninguno).
  const noFuDays = daysSince(r.last_follow_up_at ?? r.status_since);
  const fuChip = fuLabel ? (
    // Colores del semáforo de follow-up: son DATO calculado (followUpColor.ts) — no se tocan.
    <span className="badge" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'], background: FOLLOWUP_COLOR[fuState].bg, color: FOLLOWUP_COLOR[fuState].fg, border: `1px solid ${FOLLOWUP_COLOR[fuState].border}` }} title={`${t('fu.next')}: ${fmtDay(r.next_follow_up_at ?? null)}`}>
      <AlarmClock size={12} strokeWidth={1.75} /> {fmtDay(r.next_follow_up_at ?? null)} · {fuLabel}
    </span>
  ) : null;
  // Último follow-up + cuántos días lleva sin uno (visible sin desplegar el item).
  const lastFuChip = (
    <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }} title={t('fu.lastFu')}>
      <CalendarDays size={12} strokeWidth={1.75} /> {fmtDay(r.last_follow_up_at ?? null)}
    </span>
  );
  const noFuChip = noFuDays != null && fuLabel ? (
    <RChip icon={Clock4} title={t('fu.daysNoFu')}>{noFuDays}d</RChip>
  ) : null;

  const daysBadge = neutral
    ? (
      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {r.status && <span className={`badge ${statusColor(r.status)}`} style={{ fontSize: 'var(--fs-2xs)' }}>{r.status}</span>}
        {/* Por qué está acá un claim que dice "Supplementing": lo pidió Jay — en Monday la
            aprobación le ponía la etiqueta REVISION NEEDED y acá llegaban sin nada que mirar. */}
        <NeedsSupplementChip since={r.review_flag_since} />
        <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }} title={t('dept.enteredOn')}><Check size={12} strokeWidth={1.75} /> {fmtDay(r.status_since)}</span>
        {lastFuChip}
        {fuChip}
        {noFuChip}
      </span>
    )
    : (
      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {days != null && <RChip variant={days >= 7 ? 'no' : 'warn'} icon={Timer}>{days}d</RChip>}
        {lastFuChip}
        {fuChip}
        {noFuChip}
      </span>
    );
  const detail = open && (
    <div style={{ borderTop: '1px solid var(--line)', padding: view === 'cards' ? '10px 0 0' : '8px 0 4px 20px', marginTop: view === 'cards' ? 10 : 6, display: 'grid', gap: 4, fontSize: 'var(--fs-xs)' }}>
      {deptKey === 'intake' ? (
        <IntakeRowDetail claimId={r.claim_id} status={r.status || ''} address={r.address}
          contractorName={r.contractor_name} accountManager={r.account_manager || r.assigned_person || r.assigned_team_name}
          onChanged={() => onChanged?.()} />
      ) : (
        <>
          {/* Scheduling pidió la MISMA info del Case Overview en su vista (para no abrir el claim). */}
          {deptKey === 'scheduling' && <ClaimInfoHeader claimId={r.claim_id} hideName />}
          <div><span className="muted">{t('cdw.contractor')}: </span>{r.contractor_name ? <ContractorLink name={r.contractor_name} contractorId={r.contractor_id} /> : '—'}</div>
          <div><span className="muted">{t('q.insurance')}: </span>{r.insurance_company ? <InsuranceLink name={r.insurance_company} /> : '—'}</div>
          <div><span className="muted">{t('cdw.dateOfLoss')}: </span>{fmtDol(r.date_of_loss)}</div>
          <div><span className="muted">{t('scope.assigned')}: </span>{assignee}</div>
          {/* Monto en MoneyChip (Geist Mono) — principio 4/5 del rediseño. */}
          {r.rcv_amount_cents != null && <div><span className="muted">RCV: </span><MoneyChip variant="ask">{formatUsd(r.rcv_amount_cents)}</MoneyChip></div>}
          {(deptKey === 'claims' || deptKey === 'scheduling') && (
            <div style={{ marginTop: 6, borderTop: '1px solid var(--line)', paddingTop: 8 }}>
              <RequestVisitButtons claimId={r.claim_id} onDone={() => onChanged?.()} />
            </div>
          )}
          <Link to={`/claims/${r.claim_id}`} className="btn sm" style={{ marginTop: 6, justifySelf: 'start' }}>{t('cdw.open')}</Link>
        </>
      )}
    </div>
  );

  if (view === 'cards') {
    return (
      <div ref={rowRef} className="card" style={{ padding: 12, scrollMarginTop: 80 }}>
        <div role="button" tabIndex={0} onClick={() => setOpen((o) => !o)}
          onKeyDown={(e) => {
            // El textarea de 2nd opinion (y cualquier input embebido) vive DENTRO de esta fila:
            // sin este guard el preventDefault se comía los espacios al escribir (reporte de David).
            if ((e.target as HTMLElement).closest('input, textarea, select, [contenteditable="true"]')) return;
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); }
          }}
          style={{ cursor: 'pointer' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--muted)', fontSize: 'var(--fs-2xs)' }}>{open ? '▾' : '▸'}</span>
            <RowHead r={r} onClient={onClient} onChanged={onChanged} />
            <span style={{ flex: 1 }} />
            {deptKey !== 'intake' && daysBadge}
            {deptKey !== 'intake' && (
              /* En columna a la derecha, no en franja abajo (Manuel 2026-08-20): la tarjeta
                 tiene el ancho libre de ese lado y la franja horizontal lo desperdiciaba. */
              <div style={{ marginLeft: 'auto', paddingLeft: 12, borderLeft: '1px dashed var(--line)' }}>
                <FollowUpPanel r={{ claim_id: r.claim_id, status_since: r.status_since, next_follow_up_at: r.next_follow_up_at, next_follow_up_calc: r.next_follow_up_calc, last_follow_up_at: r.last_follow_up_at, last_follow_up_by: r.last_follow_up_by, last_follow_up_by_id: r.last_follow_up_by_id }}
                  deptKey={deptKey} onChanged={onChanged} vertical />
              </div>
            )}
          </div>
          {deptKey === 'intake'
            ? (
              <>
                <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 6 }} onClick={(e) => e.stopPropagation()}>
                  {r.contractor_name ? <ContractorLink name={r.contractor_name} contractorId={r.contractor_id} /> : '—'}{r.insurance_company ? <> · <InsuranceLink name={r.insurance_company} /></> : ''}{r.address ? <> · <MapPin size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {r.address}</> : ''}{r.date_of_loss ? <> · <span title={t('cdw.dateOfLoss')}>DOL {fmtDol(r.date_of_loss)}</span></> : ''}
                </div>
                {(r.phone || r.homeowner_email) && (
                  <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 4, overflowWrap: 'anywhere' }} onClick={(e) => e.stopPropagation()}>
                    {r.phone && <a href={`tel:${r.phone}`} style={{ textDecoration: 'none', color: 'inherit' }}><Phone size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {r.phone}</a>}{r.phone && r.homeowner_email ? ' · ' : ''}{r.homeowner_email && <><Mail size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {r.homeowner_email}</>}
                  </div>
                )}
                <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <IntakeBadges r={r} />
                  {/* "Working on it" visible sin desplegar también en la vista de tarjetas. */}
                  {r.status === 'Claim Review' && (
                    <span onClick={(e) => e.stopPropagation()}>
                      <WorkOnItButton claimId={r.claim_id} taskType="claim_review" label={t('work.claimReview')} />
                      {/* Pedir segunda opinión sin salir de la cola: el motivo va en la caja. */}
                      <SecondOpinionButton claimId={r.claim_id} pendingAt={r.second_opinion_at} reason={r.second_opinion_reason} />
                    </span>
                  )}
                </div>
              </>
            )
            : (
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 6 }}>
                {r.insurance_company || '—'}
                {r.date_of_loss && <> · <span title={t('cdw.dateOfLoss')}>DOL {fmtDol(r.date_of_loss)}</span></>}
                {r.assigned_team_name && (
                  <> · <Users size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {r.assigned_team_name}
                    {/* Quién lo tiene: los round-robin llevan nombre de proceso ("First Estimate"),
                        así que el equipo solo no dice a quién le toca. */}
                    {(r.assigned_position_person || r.assigned_team_people) && <span style={{ opacity: 0.8 }} title={r.assigned_team_people ?? ''}> · {r.assigned_position_person || r.assigned_team_people}</span>}
                  </>
                )}
              </div>
            )}
        </div>
        {detail}
      </div>
    );
  }
  // Lines
  return (
    <div ref={rowRef} style={{ border: '1px solid var(--line)', borderRadius: 8, background: 'var(--card)', scrollMarginTop: 80 }}>
      <div role="button" tabIndex={0} onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          // Mismo guard que en cards: no robarle el espacio/Enter a un input embebido en la fila.
          if ((e.target as HTMLElement).closest('input, textarea, select, [contenteditable="true"]')) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); }
        }}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px', cursor: 'pointer', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--muted)', fontSize: 'var(--fs-2xs)', width: 10 }}>{open ? '▾' : '▸'}</span>
        <span style={{ minWidth: 180 }}><RowHead r={r} onClient={onClient} onChanged={onChanged} /></span>
        {deptKey === 'intake' ? (
          <>
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)', minWidth: 120 }} onClick={(e) => e.stopPropagation()}>{r.contractor_name ? <ContractorLink name={r.contractor_name} contractorId={r.contractor_id} /> : '—'}</span>
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)', minWidth: 100 }} onClick={(e) => e.stopPropagation()}>{r.insurance_company ? <InsuranceLink name={r.insurance_company} /> : '—'}</span>
            {r.phone && <a className="muted" href={`tel:${r.phone}`} onClick={(e) => e.stopPropagation()} style={{ fontSize: 'var(--fs-2xs)', textDecoration: 'none' }}><Phone size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {r.phone}</a>}
            {r.homeowner_email && <span className="muted" style={{ fontSize: 'var(--fs-2xs)', overflowWrap: 'anywhere' }}><Mail size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {r.homeowner_email}</span>}
            {r.address && <span className="muted" style={{ fontSize: 'var(--fs-2xs)', flex: 1, minWidth: 120 }}><MapPin size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {r.address}</span>}
            {!r.address && <span style={{ flex: 1 }} />}
            <IntakeBadges r={r} />
            {/* "Working on it" AL FINAL de la fila, visible SIN desplegar (pedido de Manuel). */}
            {r.status === 'Claim Review' && (
              <span onClick={(e) => e.stopPropagation()}>
                <WorkOnItButton claimId={r.claim_id} taskType="claim_review" label={t('work.claimReview')} />
                {/* Pedir segunda opinión sin salir de la cola: el motivo va en la caja. */}
                <SecondOpinionButton claimId={r.claim_id} pendingAt={r.second_opinion_at} reason={r.second_opinion_reason} />
              </span>
            )}
          </>
        ) : (
          <>
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)', flex: 1, minWidth: 120 }} onClick={(e) => e.stopPropagation()}>{r.contractor_name ? <ContractorLink name={r.contractor_name} contractorId={r.contractor_id} /> : '—'}</span>
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)', minWidth: 120 }} onClick={(e) => e.stopPropagation()}>{r.insurance_company ? <InsuranceLink name={r.insurance_company} /> : '—'}</span>
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{deptKey === 'claims' ? <Users size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> : <User size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />} {assignee}</span>
            {/* EL SEGUIMIENTO CON PALABRAS, no tres chips que dicen "50d" sin decir de qué
                (pedido de Manuel 2026-08-19). Se usa el espacio libre de la derecha. */}
            <span style={{ marginLeft: 'auto' }}>
              <FollowUpPanel r={{ claim_id: r.claim_id, status_since: r.status_since, next_follow_up_at: r.next_follow_up_at, next_follow_up_calc: r.next_follow_up_calc, last_follow_up_at: r.last_follow_up_at, last_follow_up_by: r.last_follow_up_by, last_follow_up_by_id: r.last_follow_up_by_id }}
                deptKey={deptKey} onChanged={onChanged} compact />
            </span>
          </>
        )}
      </div>
      {open && <div style={{ padding: '0 12px 8px' }}>{detail}</div>}
    </div>
  );
}

/** Tarjeta "to-schedule" de Scheduling (réplica de ClaimRoute Claims): foto de la casa,
 *  cliente, dirección, claim#, días esperando y botón Schedule. */
function ToScheduleCard({ r, onClient, onScheduled }: { r: QueueRow; onClient: (id: string) => void; onScheduled: () => void }) {
  const { t } = useI18n();
  const [imgOk, setImgOk] = useState(true);
  const [scheduling, setScheduling] = useState(false);
  const days = daysSince(r.status_since);
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'relative', height: 150, background: 'var(--bg)' }}>
        {r.photo_url && imgOk
          ? <img src={r.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setImgOk(false)} />
          : <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', color: 'var(--muted)', fontSize: 'var(--fs-xl)' }}><Home size={30} strokeWidth={1.75} /></div>}
        {/* El cartel dice QUÉ VISITA FALTA (wait_kind, derivado de la evidencia), no cómo quedó
            etiquetado el board: 9 de 14 "Re-inspection requested" eran primeras visitas. */}
        {(r.wait_kind || r.status) && (() => {
          const e = r.wait_kind ? ESPERA_ETIQUETAS[r.wait_kind] : null;
          return <span className="badge" style={{ position: 'absolute', top: 8, left: 8, background: 'var(--orange-soft)', color: e?.color ?? 'var(--orange)', fontWeight: 'var(--fw-bold)' }}>{e ? t(e.clave) : r.status}</span>;
        })()}
        {days != null && <span className="badge" style={{ position: 'absolute', top: 8, right: 8, background: days >= 7 ? 'var(--red-soft)' : 'var(--card)', color: days >= 7 ? 'var(--red)' : 'var(--muted)', fontWeight: 'var(--fw-bold)' }}><Timer size={12} strokeWidth={1.75} /> {days}d</span>}
      </div>
      <div style={{ padding: 12, display: 'grid', gap: 4, flex: 1 }}>
        <button type="button" onClick={() => onClient(r.claim_id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--ink)', font: 'inherit', fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-base)', textAlign: 'left' }}>
          {r.homeowner_name || r.claim_number || `#${r.claim_id}`}
        </button>
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{r.address || r.insurance_company || '—'}</div>
        {r.claim_number
          ? <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-mono)' }}>#{r.claim_number}</div>
          /* Sin claim number no hay nada que agendar todavía: el seguro no tiene el claim. */
          : <span className="badge" style={{ justifySelf: 'start', background: 'var(--orange-soft)', color: 'var(--orange)', fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-2xs)' }} title={t('sched.notFiledHint')}>{t('sched.notFiled')}</span>}
        <span style={{ flex: 1 }} />
        <button type="button" onClick={() => setScheduling(true)} className="btn" style={{ background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent', justifyContent: 'center', marginTop: 6, fontWeight: 'var(--fw-bold)' }}>{t('cr.schedule')}</button>
      </div>
      {scheduling && (
        <ScheduleModal claimId={r.claim_id} title={r.homeowner_name || r.claim_number || undefined}
          defaultType={r.status ? SCHED_STATUS_TO_APPT_TYPE[r.status] : undefined}
          onClose={() => setScheduling(false)} onDone={() => { setScheduling(false); onScheduled(); }} />
      )}
    </div>
  );
}

type RangeKind = 'all' | 'today' | 'week' | 'lastWeek' | 'month' | 'lastMonth' | 'custom';
// Pestañas de Claim Review (All / Working on / Done) — datos de /api/claim-review/activity.
type CrWorking = { claim_id: string; last_by: string | null; last_at: string; actions: number };
type CrDone = { claim_id: string; homeowner_name: string | null; claim_number: string | null; decision: string; reason: string | null; by: string | null; decided_at: string; hours: number | null; active_hours: number | null; touches: number };
const startOfWeek = (): number => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - ((d.getDay() + 6) % 7)); return d.getTime(); }; // lunes
const startOfMonth = (): number => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).getTime(); };
// Rangos extra del dropdown móvil (pedido del dueño: elegir rápido hoy/semana pasada/mes pasado).
const startOfToday = (): number => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); };
const startOfLastWeek = (): number => startOfWeek() - 7 * 24 * 3600 * 1000;
const startOfLastMonth = (): number => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() - 1, 1).getTime(); };

/**
 * Wrapper que RE-MONTA la cola al cambiar de depto/status (React key): así cada cola arranca
 * limpia leyendo SU memoria persistida (sessionStorage por cola) — y al volver con "atrás",
 * la cola restaura selección/filtros/vista tal como estaban (queja masiva del equipo).
 */
export function DepartmentQueue() {
  const { key, status } = useParams();
  return <DepartmentQueueInner key={`${key ?? ''}::${status ?? ''}`} />;
}

function DepartmentQueueInner() {
  const { key, status } = useParams();
  const { t } = useI18n();
  const nav = useNavigate();
  const loc = useLocation();
  // "← Back": aparece solo cuando llegaste a esta cola desde OTRA vista (ej. el chip de views
  // del panel del claim navega con state.back) → nav(-1) devuelve a la vista anterior exacta.
  const cameFromNav = (loc.state as { back?: boolean } | null)?.back === true;
  const decStatus = decodeURIComponent(status ?? '');
  // Prefijo de la memoria de ESTA cola (cada cola recuerda su propio estado).
  const mem = `dq:${key ?? ''}:${decStatus}`;
  // Insurance inspection (Claims): vista calendario por defecto, ubicada por la fecha de la
  // cita de inspección (scheduled_start). Cada claims team ya ve solo los suyos (backend).
  const claimsInspection = key === 'claims' && decStatus === 'Insurance inspection';
  const [rows, setRows] = useState<QueueRow[]>([]);
  // Si el backend tuvo que cortar la lista, se dice. Antes se cortaba en 200 en silencio y lo que
  // se caía eran los claims MÁS NUEVOS — trabajo que desaparecía de la cola donde se lo busca.
  const [corte, setCorte] = useState<{ mostrando: number; total: number | null } | null>(null);
  const [loading, setLoading] = useState(true);
  // Vista por cola (sobrevive "atrás"); default: calendario en Insurance inspection, si no el
  // último modo global elegido (localStorage dept.view).
  const [view, setView] = usePersistentState<'cards' | 'lines' | 'calendar'>(`${mem}:view`,
    claimsInspection ? 'calendar' : ((localStorage.getItem('dept.view') as 'cards' | 'lines') || 'lines'));
  const setViewPersist = (v: 'cards' | 'lines' | 'calendar') => { setView(v); localStorage.setItem('dept.view', v); };
  // Selección/filtros PERSISTEN por cola → al volver del claim completo la cola queda igual.
  const [sel, setSel] = usePersistentState<string | null>(`${mem}:sel`, null);
  /**
   * ABRIR UN CLAIM DESDE OTRA PANTALLA — pedido de Manuel (2026-08-24): "quiero poder darle click
   * al botón amarillo que dice Claim Review y que me lleve al working view de Claim Review con el
   * cliente abierto ahí".
   *
   * La selección vive en localStorage por cola (para que al volver de un claim la cola quede
   * igual), así que llegar "con el cliente abierto" necesitaba una vía de entrada: `?claim=<id>`.
   * Se consume UNA vez y se borra de la barra de direcciones — si se quedara pegado, recargar o
   * volver con el botón del navegador reabriría un claim que la persona ya había cerrado.
   */
  const [urlParams, setUrlParams] = useSearchParams();
  useEffect(() => {
    const pedido = urlParams.get('claim');
    if (!pedido) return;
    setSel(pedido);
    const limpio = new URLSearchParams(urlParams);
    limpio.delete('claim');
    setUrlParams(limpio, { replace: true });
  }, [urlParams, setUrlParams, setSel]);
  const [range, setRange] = usePersistentState<RangeKind>(`${mem}:range`, 'all');
  const [from, setFrom] = usePersistentState<string>(`${mem}:from`, '');
  const [to, setTo] = usePersistentState<string>(`${mem}:to`, '');
  const [q, setQ] = usePersistentState<string>(`${mem}:q`, '');
  const [typeFilter, setTypeFilter] = usePersistentState<string>(`${mem}:type`, '');
  /** Waiting to Schedule: "To do" = fileados con el seguro (hay claim number), listos para agendar;
   *  "All" incluye los que todavía no se filearon (Paula 2026-09-15). */
  const [schedTab, setSchedTab] = usePersistentState<'todo' | 'all'>(`${mem}:schedTab`, 'todo');
  // Filtro por ESTATUS DEL CLAIM en Waiting to Schedule (Claim in / Re-inspection / etc.).
  const [claimStFilter, setClaimStFilter] = usePersistentState<string>(`${mem}:claimSt`, '');
  // Filtro por EQUIPO asignado — disponible en TODAS las colas de status (pedido del equipo:
  // "me fui a Waiting Project Completion y no puedo filtrar los claims de mi equipo").
  const [teamFilter, setTeamFilter] = usePersistentState<string>(`${mem}:team`, '');
  // Filtro por CONTRATISTA — disponible en TODAS las colas de status (pedido de Manuel).
  const [coFilter, setCoFilter] = usePersistentState<string>(`${mem}:co`, '');
  // Filtro por ASEGURADORA (pedido de Scheduling 2026-08-13, cola Claim in: "pedir la info de
  // varios adjusters en una sola llamada" — se agrupan los claims del mismo seguro).
  const [insFilter, setInsFilter] = usePersistentState<string>(`${mem}:ins`, '');
  // Filtro por INSPECTOR — solo en Insurance inspection (Claims): quién asiste a la cita.
  const [inspectorFilter, setInspectorFilter] = usePersistentState<string>(`${mem}:insp`, '');
  // La "etapa de origen": Re-inspection requested viene del cd.status; el resto, del lifecycle.
  /**
   * EL CARTEL DICE QUÉ HAY QUE HACER, NO CÓMO QUEDÓ ETIQUETADO EL BOARD.
   *
   * Antes: si el estatus decía 'Re-inspection requested' se mostraba tal cual. Medido en prod, de
   * 14 claims así marcados solo 5 habían tenido una inspección: los otros 9 esperaban su PRIMERA
   * visita y el equipo de scheduling leía "re-inspección". `wait_kind` lo deriva en el backend
   * (lib/tipoDeEspera) con la misma fórmula que el panel de los dashboards.
   */
  const claimStOf = (r: QueueRow): string => (
    r.wait_kind ? t(ESPERA_ETIQUETAS[r.wait_kind]?.clave ?? 'cdash.tagAdjuster')
      : (r.status || r.lifecycle_status || '—'));
  const isSchedQueue = key === 'scheduling' && decStatus === 'Waiting to Schedule';
  // Welcome Call (feedback 2026-07-30): por DEFECTO solo los que la NECESITAN — claims en
  // Claim Accepted o Claim in (aceptados, todavía sin llegar a Insurance inspection). La
  // pestañita "All" muestra la cola completa como antes.
  const isWelcomeCallQueue = key === 'scheduling' && decStatus === 'Welcome Call';
  const WC_NEEDED = ['Claim Accepted', 'Claim in'];
  const [wcTab, setWcTab] = usePersistentState<'needed' | 'all'>(`${mem}:wcTab`, 'needed');
  // Vistas con render propio (secciones): sin toggle de cards/lines/calendario.
  const customSection = key === 'scheduling' && ['Welcome Call', 'Ready to be filed', 'Waiting to Schedule'].includes(decStatus);
  // Waiting approval (Claims) tiene render propio PERO mantiene el toggle cards/lines.
  const claimsWaitingApproval = key === 'claims' && decStatus === 'Waiting approval';
  const claimsAtrStage = key === 'claims' && (decStatus === 'Review ATR' || decStatus === 'ATR Follow up' || decStatus === 'Manager Review') ? decStatus : null;

  // Colas de supervisión de Claims (Approved, etc.): no hay follow-up, se filtran
  // por fecha de aprobación (status_since) en vez de urgencia.
  const neutral = key === 'claims';
  const [reloadTick, setReloadTick] = useState(0);
  // PESTAÑAS de Claim Review (pedido de Manuel 2026-08-11): All / Working on / Done.
  // Done = quién decidió qué y cuánto tardó desde que el claim entró a la etapa.
  const isClaimReviewQueue = decodeURIComponent(status ?? '') === 'Claim Review';
  const [crTab, setCrTab] = useState<'all' | 'working' | 'done'>('all');
  // Día del tab Done (pedido 2026-08-11: "poder filtrarlo por día y ver los días anteriores").
  // null = las últimas decisiones, sin filtrar. El filtro viaja al BACKEND: sin día se devuelven
  // las últimas 300 y un día viejo queda fuera de ese tope, así que recortar acá mostraría un día
  // vacío que en realidad tuvo trabajo.
  const [crDay, setCrDay] = useState<string | null>(null);
  const [crAct, setCrAct] = useState<{ working: CrWorking[]; done: CrDone[]; done_days: Array<{ day: string; n: number }> } | null>(null);
  useEffect(() => {
    if (!isClaimReviewQueue) return;
    api.get<{ working: CrWorking[]; done: CrDone[]; done_days: Array<{ day: string; n: number }> }>(
      `/api/claim-review/activity${crDay ? `?day=${crDay}` : ''}`).then(setCrAct).catch(() => {});
  }, [isClaimReviewQueue, reloadTick, crDay]);
  const [addLead, setAddLead] = useState(false);
  const [viewSettings, setViewSettings] = useState(false); // ⚙ de la working view
  const isLeadQueue = key === 'intake' && decStatus === 'Lead';

  // Solo FETCH — ya no resetea filtros/selección: el wrapper re-monta al cambiar de cola y la
  // memoria por-cola restaura el estado al volver con "atrás".
  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get<{ rows: QueueRow[]; nextCursor: string | null; truncado?: boolean; total?: number | null }>(
        `/api/departments/${key}/queue?status=${encodeURIComponent(status || '')}`,
      )
      .then((r) => {
        if (!alive) return;
        setRows(r.rows);
        setCorte(r.truncado ? { mostrando: r.rows.length, total: r.total ?? null } : null);
      })
      .catch(() => { if (alive) { setRows([]); setCorte(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [key, status, reloadTick]);

  /* ⚠️ UN SOLO PREDICADO PARA LA LISTA Y PARA TODOS LOS CONTADORES.
     ESTA PANTALLA ES **TODAS** LAS COLAS DE **TODOS** LOS DEPARTAMENTOS (Claims, Payments,
     Scheduling, Intake, COC), así que el bug se veía en todas: la lista salía de `filtered` pero
     los seis grupos de chips contaban sobre `rows`, la respuesta CRUDA del endpoint. Elegías un
     equipo, el título bajaba a 12 y los chips seguían diciendo 338 — el total de la empresa.

     `salvo` apaga UNA dimensión: es lo que hace que el contador de cada filtro sea útil en vez de
     circular. El chip "Maria's Team 12" tiene que responder "cuántos me quedan si elijo Maria's
     Team, con los OTROS filtros puestos"; si se contara a sí mismo daría el total de la cola
     cuando no hay nada elegido y su propio número cuando sí, que no le sirve a nadie. Es la misma
     razón por la que en Supplements la pestaña queda afuera del alcance. */
  const qLower = q.trim().toLowerCase();
  type Dim = 'q' | 'tipo' | 'claimSt' | 'wc' | 'team' | 'co' | 'ins' | 'inspector' | 'fecha' | 'sched';
  // El buscador también encuentra por TELÉFONO del homeowner (Paula 2026-09-15): se comparan solo dígitos.
  const qDigits = qLower.replace(/\D/g, '');
  const pasa = (r: QueueRow, salvo?: Dim): boolean => {
    if (salvo !== 'q' && qLower && !`${r.homeowner_name ?? ''} ${r.address ?? ''} ${r.claim_number ?? ''}`.toLowerCase().includes(qLower)
        && !(qDigits.length >= 4 && (r.phone ?? '').replace(/\D/g, '').includes(qDigits))) return false;
    if (salvo !== 'sched' && isSchedQueue && schedTab === 'todo' && !r.claim_number) return false;
    if (salvo !== 'tipo' && typeFilter && canonSchedType(r.sched_type) !== typeFilter) return false;
    if (salvo !== 'claimSt' && claimStFilter && claimStOf(r) !== claimStFilter) return false;
    if (salvo !== 'wc' && isWelcomeCallQueue && wcTab === 'needed' && !WC_NEEDED.includes(r.lifecycle_status || '')) return false;
    if (salvo !== 'team' && teamFilter && (r.assigned_team_name || '—') !== teamFilter) return false;
    if (salvo !== 'co' && coFilter && (r.contractor_name || '—') !== coFilter) return false;
    if (salvo !== 'ins' && insFilter && (r.insurance_company || '—') !== insFilter) return false;
    if (salvo !== 'inspector' && inspectorFilter && (r.scheduled_inspector || '—') !== inspectorFilter) return false;
    if (salvo === 'fecha' || range === 'all') return true;
    const ts = r.status_since ? new Date(r.status_since).getTime() : null;
    if (ts == null) return false;
    if (range === 'today') return ts >= startOfToday();
    if (range === 'week') return ts >= startOfWeek();
    if (range === 'lastWeek') return ts >= startOfLastWeek() && ts < startOfWeek();
    if (range === 'month') return ts >= startOfMonth();
    if (range === 'lastMonth') return ts >= startOfLastMonth() && ts < startOfMonth();
    // custom
    const lo = from ? new Date(`${from}T00:00:00`).getTime() : -Infinity;
    const hi = to ? new Date(`${to}T23:59:59`).getTime() : Infinity;
    return ts >= lo && ts <= hi;
  };
  const filteredBase = rows.filter((r) => pasa(r));
  // Working on = los que tienen actividad de un usuario desde que entraron a Claim Review.
  const crWorkingMap = new Map((crAct?.working ?? []).map((w) => [w.claim_id, w]));
  // El buscador de arriba también recorta el Done: con 249 decisiones, buscar un cliente y que la
  // tabla no reaccione se lee como que el buscador está roto.
  const crDoneVisible = (crAct?.done ?? []).filter((r) => {
    const t2 = q.trim().toLowerCase();
    if (!t2) return true;
    return `${r.homeowner_name ?? ''} ${r.claim_number ?? ''} ${r.by ?? ''}`.toLowerCase().includes(t2);
  });
  const filtradoSinOrden = isClaimReviewQueue && crTab === 'working'
    ? filteredBase.filter((r) => crWorkingMap.has(String((r as { claim_id: string }).claim_id)))
    : filteredBase;
  /**
   * EL ORDEN DE LA COLA (Manuel 2026-08-20): "quiero ordenar de más viejos a más nuevos, y de
   * follow ups vencidos los más viejos a los más nuevos".
   *
   * Dos reglas, en este orden:
   *  1. Un follow-up VENCIDO va antes que cualquier fila sin vencer, y entre vencidos manda el
   *     más viejo — el que lleva más tiempo esperando respuesta es el que más urge.
   *  2. Todo lo demás, por antigüedad en el status: el que lleva más días, primero.
   *
   * Sin fecha de status va al final: no se puede afirmar que sea viejo, y colarlo arriba sería
   * inventarle una antigüedad.
   */
  const t0 = Date.now();
  const vencidoEn = (r: QueueRow): number | null => {
    const ts = r.next_follow_up_at ? new Date(r.next_follow_up_at).getTime() : null;
    return ts != null && ts <= t0 ? ts : null;
  };
  const filtered = [...filtradoSinOrden].sort((a, b) => {
    const va = vencidoEn(a);
    const vb = vencidoEn(b);
    if (va != null && vb != null) return va - vb;   // el vencido hace más tiempo, primero
    if (va != null) return -1;
    if (vb != null) return 1;
    const sa = a.status_since ? new Date(a.status_since).getTime() : Infinity;
    const sb = b.status_since ? new Date(b.status_since).getTime() : Infinity;
    return sa - sb;                                  // más viejo → más nuevo
  });
  /** Las filas que verías si cambiaras SOLO ese filtro. Es la base de los chips de esa dimensión. */
  const baseDe = (d: Dim) => rows.filter((r) => pasa(r, d));

  // Selección múltiple sobre lo que se ve en la cola (ya filtrado).
  const bulk = useBulkSelect(filtered.map((r) => String((r as { claim_id: string }).claim_id)));
  // Conteos por tipo para las pastillas: todo lo demás aplicado, el tipo no.
  const typeCounts = (() => {
    const base = baseDe('tipo');
    const m: Record<string, number> = {};
    for (const r of base) { const tp = canonSchedType(r.sched_type); m[tp] = (m[tp] ?? 0) + 1; }
    return { total: base.length, m };
  })();

  const viewToggle = (
    <div className="dq-view" style={{ display: 'inline-flex', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
      <button className={`btn ghost sm${view === 'lines' ? ' active' : ''}`} style={{ borderRadius: 0 }} onClick={() => setViewPersist('lines')}><List size={14} strokeWidth={1.75} /> {t('sup.viewLines')}</button>
      <button className={`btn ghost sm${view === 'cards' ? ' active' : ''}`} style={{ borderRadius: 0 }} onClick={() => setViewPersist('cards')}><LayoutGrid size={14} strokeWidth={1.75} /> {t('sup.viewCards')}</button>
      {(key === 'intake' || claimsInspection) && <button className={`btn ghost sm${view === 'calendar' ? ' active' : ''}`} style={{ borderRadius: 0 }} onClick={() => setViewPersist('calendar')}><CalendarDays size={14} strokeWidth={1.75} /> {t('dept.viewCalendar')}</button>}
    </div>
  );

  // Cola COC: render 100% propio (claim_coc, secciones To Send/Sent, sin follow-up).
  // Va después de todos los hooks para no romper las reglas de hooks.
  if (key === 'claims' && decStatus === 'COC') return <CocQueueSection />;
  // Cola "To Sign" (Intake): contratos firmados por el homeowner esperando la firma del PA.
  if (key === 'intake' && decStatus === 'To Sign') return <ToSignSection />;

  const rangeBtn = (k: RangeKind, label: string) => (
    <button className={`btn ghost sm${range === k ? ' active' : ''}`} style={{ borderRadius: 0 }} onClick={() => setRange(k)}>{label}</button>
  );
  const dateFilter = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      {/* DESKTOP: los 4 botones de siempre. MÓVIL: un dropdown en una sola línea (pedido del
          dueño 2026-08-08: "ese espacio me ocupa mucha pantalla") con rangos extra. */}
      <div className="dq-range" style={{ display: 'inline-flex', border: '1px solid var(--line)', borderRadius: 8, overflow: 'hidden' }}>
        {rangeBtn('all', t('dept.rangeAll'))}
        {rangeBtn('week', t('dept.rangeWeek'))}
        {rangeBtn('month', t('dept.rangeMonth'))}
        {rangeBtn('custom', t('dept.rangeDate'))}
      </div>
      <select className="input sm dq-range-select" value={range} onChange={(e) => setRange(e.target.value as RangeKind)}>
        <option value="all">{t('dept.rangeAll')}</option>
        <option value="today">{t('dept.rangeToday')}</option>
        <option value="week">{t('dept.rangeWeek')}</option>
        <option value="lastWeek">{t('dept.rangeLastWeek')}</option>
        <option value="month">{t('dept.rangeMonth')}</option>
        <option value="lastMonth">{t('dept.rangeLastMonth')}</option>
        <option value="custom">{t('dept.rangeDate')}</option>
      </select>
      {range === 'custom' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="date" className="input sm" value={from} onChange={(e) => setFrom(e.target.value)} title={t('dept.from')} />
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>→</span>
          <input type="date" className="input sm" value={to} onChange={(e) => setTo(e.target.value)} title={t('dept.to')} />
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Cabecera GRANDE con el color del status y STICKY (pedido de Manuel: "se me olvida en
            qué área estoy") — el nombre de la cola queda visible aun scrolleando la lista. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap', position: 'sticky', top: 60, zIndex: 4, background: 'var(--bg)', padding: '8px 0' }}>
          {/* Volver a la vista anterior (ej. el board de Claims con el panel abierto). */}
          {cameFromNav && (
            <button className="btn ghost sm" onClick={() => nav(-1)} title={t('common.back')} style={{ fontWeight: 700 }}>← {t('common.back')}</button>
          )}
          <div className={`badge ${statusColor(status ?? null)}`}
            style={{ fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-bold)', padding: '8px 20px', borderRadius: 14, letterSpacing: -0.3, display: 'inline-flex', gap: 10, alignItems: 'baseline', lineHeight: 1.15 }}>
            <span style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.8, opacity: 0.7 }}>{key} ▸</span>
            {decStatus}
            <span style={{ fontSize: 'var(--fs-base)', fontWeight: 'var(--fw-bold)', opacity: 0.85 }}>· {filtered.length}{range !== 'all' ? ` / ${rows.length}` : ''}</span>
          </div>
          <span style={{ flex: 1 }} />
          {isLeadQueue && <button className="btn sm" onClick={() => setAddLead(true)}><Plus size={14} strokeWidth={1.75} /> {t('al.addLead')}</button>}
          {!customSection && dateFilter}
          {!customSection && viewToggle}
          {/* Quién recibe las segundas opiniones: solo tiene sentido en la cola donde se piden.
              En móvil se esconde (dq-so): ocupa media línea y es un ajuste de una vez. */}
          {decStatus === 'Claim Review' && <span className="dq-so"><SecondOpinionSetting /></span>}
          {/* ⚙ de ESTA vista de trabajo: sus plantillas de email y sus días de follow-up. */}
          <ViewSettingsButton onClick={() => setViewSettings(true)} />
        </div>
        {viewSettings && key && (
          <ViewSettingsModal deptKey={key} viewKey={decStatus} label={decStatus} onClose={() => setViewSettings(false)} />
        )}
        {/* PESTAÑAS de Claim Review: All / Working on / Done (pedido de Manuel 2026-08-11). */}
        {isClaimReviewQueue && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
            {([['all', t('crv.all'), filteredBase.length],
               ['working', t('crv.working'), filteredBase.filter((r) => crWorkingMap.has(String((r as { claim_id: string }).claim_id))).length],
               ['done', t('crv.done'), crDoneVisible.length]] as Array<['all' | 'working' | 'done', string, number]>).map(([k2, label, n]) => (
              <button key={k2} className={`chipx${crTab === k2 ? ' active' : ''}`} onClick={() => setCrTab(k2)}>
                {label} <span style={{ opacity: 0.7 }}>{n}</span>
              </button>
            ))}
          </div>
        )}
        {isSchedQueue && (() => {
          const base = baseDe('sched');
          const nTodo = base.filter((r) => !!r.claim_number).length;
          return (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8, alignItems: 'center' }}>
              {([['todo', t('sched.todo'), nTodo], ['all', t('sched.allTab'), base.length]] as Array<['todo' | 'all', string, number]>).map(([k2, label, n]) => (
                <button key={k2} className={`chipx${schedTab === k2 ? ' active' : ''}`} onClick={() => setSchedTab(k2)} title={t('sched.todoHint')}>
                  {label} <span style={{ opacity: 0.7 }}>{n}</span>
                </button>
              ))}
              {base.length - nTodo > 0 && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('sched.notFiledN', { n: String(base.length - nTodo) })}</span>}
            </div>
          );
        })()}
        {isSchedQueue && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {[['', t('cr.filter.all')] as [string, string], ...SCHED_TYPES.map((k2) => [k2, t(`schType.${k2}`)] as [string, string])].map(([k2, label]) => (
              <button key={k2 || 'all'} className={`chipx${typeFilter === k2 ? ' active' : ''}`} onClick={() => setTypeFilter(k2)}>
                {label} <span style={{ opacity: 0.7 }}>{k2 === '' ? typeCounts.total : (typeCounts.m[k2] ?? 0)}</span>
              </button>
            ))}
          </div>
        )}
        {/* TODOS LOS FILTROS EN UNA SOLA LÍNEA (Manuel 2026-08-20: "esto ponmelo todo en la
            misma línea"). Antes cada filtro se llevaba su propio renglón — buscador, equipo,
            contratista, aseguradora, inspector — y la cola empezaba media pantalla más abajo.
            `flexWrap` los baja solos en pantalla angosta, así que no se pierde ninguno. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          {/* Buscador compartido por TODAS las vistas de cola (nombre / dirección / #claim). */}
          <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200, maxWidth: 460 }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)', fontSize: 'var(--fs-sm)', pointerEvents: 'none' }}><Search size={14} strokeWidth={1.75} style={{ display: 'block' }} /></span>
            <input className="input" style={{ width: '100%', paddingLeft: 34 }} placeholder={t('dept.search')} value={q} onChange={(e) => setQ(e.target.value)} />
            {q && <button className="btn ghost sm" style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', padding: '2px 8px' }} onClick={() => setQ('')}><X size={14} strokeWidth={1.75} /></button>}
          </div>
        {/* Filtro por EQUIPO asignado — en TODAS las colas (aparece cuando hay 2+ equipos). */}
        {(() => {
          // Todo lo demás aplicado; el equipo no (si no, cada chip se contaría a sí mismo).
          const base = baseDe('team');
          const counts = new Map<string, number>();
          for (const r of base) { const k2 = r.assigned_team_name || '—'; counts.set(k2, (counts.get(k2) ?? 0) + 1); }
          const keys = [...counts.keys()].sort((a, b) => (a === '—' ? 1 : b === '—' ? -1 : a.localeCompare(b)));
          if (keys.length < 2) return null;
          return (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}><Users size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('dept.teamFilter')}:</span>
              <button className={`chipx${teamFilter === '' ? ' active' : ''}`} onClick={() => setTeamFilter('')}>{t('cr.filter.all')} <span style={{ opacity: 0.7 }}>{base.length}</span></button>
              {keys.map((k2) => (
                <button key={k2} className={`chipx${teamFilter === k2 ? ' active' : ''}`} onClick={() => setTeamFilter(k2)}>
                  {k2} <span style={{ opacity: 0.7 }}>{counts.get(k2)}</span>
                </button>
              ))}
            </div>
          );
        })()}
        {/* Filtro por CONTRATISTA — en TODAS las colas (dropdown: pueden ser muchos). */}
        {(() => {
          const base = baseDe('co');
          const counts = new Map<string, number>();
          for (const r of base) { const k2 = r.contractor_name || '—'; counts.set(k2, (counts.get(k2) ?? 0) + 1); }
          const keys = [...counts.keys()].sort((a, b) => (a === '—' ? 1 : b === '—' ? -1 : a.localeCompare(b)));
          if (keys.length < 2) return null;
          return (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}><Building2 size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('cdw.contractor')}:</span>
              <select className="input sm" style={{ maxWidth: 280 }} value={coFilter} onChange={(e) => setCoFilter(e.target.value)}>
                <option value="">{t('cr.filter.all')} ({base.length})</option>
                {keys.map((k2) => <option key={k2} value={k2}>{k2} ({counts.get(k2)})</option>)}
              </select>
              {coFilter && <button className="btn ghost sm" onClick={() => setCoFilter('')}><X size={14} strokeWidth={1.75} /></button>}
            </div>
          );
        })()}
        {/* Filtro por ASEGURADORA — en TODAS las colas (pedido de Scheduling 2026-08-13: agrupar
            los claims del mismo seguro para pedir la info de varios adjusters en UNA llamada). */}
        {(() => {
          const base = baseDe('ins');
          const counts = new Map<string, number>();
          for (const r of base) { const k2 = r.insurance_company || '—'; counts.set(k2, (counts.get(k2) ?? 0) + 1); }
          const keys = [...counts.keys()].sort((a, b) => (a === '—' ? 1 : b === '—' ? -1 : a.localeCompare(b)));
          if (keys.length < 2) return null;
          return (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}><Landmark size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('q.insurance')}:</span>
              <select className="input sm" style={{ maxWidth: 280 }} value={insFilter} onChange={(e) => setInsFilter(e.target.value)}>
                <option value="">{t('cr.filter.all')} ({base.length})</option>
                {keys.map((k2) => <option key={k2} value={k2}>{k2} ({counts.get(k2)})</option>)}
              </select>
              {insFilter && <button className="btn ghost sm" onClick={() => setInsFilter('')}><X size={14} strokeWidth={1.75} /></button>}
            </div>
          );
        })()}
        {/* Filtro por INSPECTOR — solo en Insurance inspection (Claims): quién asiste a la cita. */}
        {claimsInspection && (() => {
          const base = baseDe('inspector');
          const counts = new Map<string, number>();
          for (const r of base) { const k2 = r.scheduled_inspector || '—'; counts.set(k2, (counts.get(k2) ?? 0) + 1); }
          const keys = [...counts.keys()].sort((a, b) => (a === '—' ? 1 : b === '—' ? -1 : a.localeCompare(b)));
          if (keys.length < 2) return null;
          return (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}><HardHat size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('insp.filterByInspector')}:</span>
              <select className="input sm" style={{ maxWidth: 280 }} value={inspectorFilter} onChange={(e) => setInspectorFilter(e.target.value)}>
                <option value="">{t('insp.allInspectors')} ({base.length})</option>
                {keys.map((k2) => <option key={k2} value={k2}>{k2} ({counts.get(k2)})</option>)}
              </select>
              {inspectorFilter && <button className="btn ghost sm" onClick={() => setInspectorFilter('')}><X size={14} strokeWidth={1.75} /></button>}
            </div>
          );
        })()}
        </div>
        {/* Filtro por ESTATUS DEL CLAIM (Claim in / Re-inspection requested / …) — pedido de Scheduling
            para trabajar la cola por tipo de caso sin mezclar. */}
        {isWelcomeCallQueue && (() => {
          const base = baseDe('wc');
          const needed = base.filter((r) => WC_NEEDED.includes(r.lifecycle_status || '')).length;
          return (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
              <button className={`chipx${wcTab === 'needed' ? ' active' : ''}`} onClick={() => setWcTab('needed')}>
                <Phone size={14} strokeWidth={1.75} /> {t('wc.tabNeeded')} <span style={{ opacity: 0.7 }}>{needed}</span>
              </button>
              <button className={`chipx${wcTab === 'all' ? ' active' : ''}`} onClick={() => setWcTab('all')}>
                {t('cr.filter.all')} <span style={{ opacity: 0.7 }}>{base.length}</span>
              </button>
              {wcTab === 'needed' && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('wc.tabNeededHint')}</span>}
            </div>
          );
        })()}
        {isSchedQueue && (() => {
          const base = baseDe('claimSt');
          const counts = new Map<string, number>();
          for (const r of base) counts.set(claimStOf(r), (counts.get(claimStOf(r)) ?? 0) + 1);
          const keys = [...counts.keys()].sort();
          if (keys.length < 2) return null;
          return (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('dept.claimStatus')}:</span>
              <button className={`chipx${claimStFilter === '' ? ' active' : ''}`} onClick={() => setClaimStFilter('')}>{t('cr.filter.all')} <span style={{ opacity: 0.7 }}>{base.length}</span></button>
              {keys.map((k2) => (
                <button key={k2} className={`chipx${claimStFilter === k2 ? ' active' : ''}`} onClick={() => setClaimStFilter(k2)}>
                  {k2} <span style={{ opacity: 0.7 }}>{counts.get(k2)}</span>
                </button>
              ))}
            </div>
          );
        })()}
        {/* AVISO DE CORTE. Que la lista esté incompleta tiene que verse: una cola truncada no se ve
            rota, se ve corta, y nadie extraña lo que no sabe que existe. */}
        {corte && (
          <div style={{ margin: '6px 0', padding: '7px 11px', borderRadius: 8, fontSize: 'var(--fs-xs)',
            background: 'var(--orange-soft)', border: '1px solid var(--orange)', color: 'var(--orange)' }}>
            <AlertTriangle size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('dept.truncated')
              .replace('{n}', String(corte.mostrando))
              .replace('{total}', corte.total != null ? String(corte.total) : '?')}
          </div>
        )}
        {isClaimReviewQueue && crTab === 'done' ? (
          <ClaimReviewDone rows={crDoneVisible} dias={crAct?.done_days ?? []}
            day={crDay} onDay={setCrDay} onClient={setSel} />
        ) : loading ? (
          <div className="page-empty">{t('common.loadingDots')}</div>
        ) : filtered.length === 0 ? (
          <div className="page-empty">{rows.length === 0 ? t('dept.noClaims') : t('dept.noInRange')}</div>
        ) : key === 'scheduling' && decodeURIComponent(status ?? '') === 'Welcome Call' ? (
          <WelcomeCallSection rows={filtered as unknown as FlowRow[]} onChanged={() => setReloadTick((n) => n + 1)} />
        ) : key === 'scheduling' && decodeURIComponent(status ?? '') === 'Ready to be filed' ? (
          <ReadyToFileSection rows={filtered as unknown as FlowRow[]} onChanged={() => setReloadTick((n) => n + 1)} />
        ) : claimsWaitingApproval ? (
          <ClaimsWaitingApprovalSection rows={filtered as unknown as FlowRow[]} view={view === 'cards' ? 'cards' : 'lines'} onChanged={() => setReloadTick((n) => n + 1)} onClient={setSel} />
        ) : claimsAtrStage ? (
          <ClaimsAtrSection rows={filtered as unknown as FlowRow[]} stage={claimsAtrStage} view={view === 'cards' ? 'cards' : 'lines'} onChanged={() => setReloadTick((n) => n + 1)} onClient={setSel} />
        ) : key === 'scheduling' && decodeURIComponent(status ?? '') === 'Waiting to Schedule' ? (
          // Vista "Waiting to Schedule": tarjetas ricas (foto + Schedule), como ClaimRoute.
          // Orden de MÁS VIEJO a MÁS NUEVO (el que lleva más tiempo esperando va primero),
          // para saber en qué orden agendarlos.
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14, alignItems: 'start' }}>
            {[...filtered].sort((a, b) => new Date(a.status_since || 0).getTime() - new Date(b.status_since || 0).getTime())
              .map((r) => <ToScheduleCard key={r.presence_id} r={r} onClient={setSel} onScheduled={() => setReloadTick((n) => n + 1)} />)}
          </div>
        ) : view === 'calendar' ? (
          <BulkProvider sel={bulk}>
          <CalendarView
            items={filtered}
            dateOf={(r) => (claimsInspection ? (r.scheduled_start ?? r.status_since) : r.status_since)}
            initialMode={claimsInspection ? 'week' : 'all'}
            initialOffset={claimsInspection ? 1 : 0}
            grid={claimsInspection}
            fullWidth={key === 'claims'}
            searchText={(r) => `${r.homeowner_name ?? ''} ${r.address ?? ''} ${r.claim_number ?? ''}`}
            countLabel={status ? decodeURIComponent(status) : ''}
            view="lines"
            renderItem={(r) => <QueueItem key={r.presence_id} r={r} view="lines" onClient={setSel} neutral={neutral} deptKey={key} onChanged={() => setReloadTick((n) => n + 1)} />}
          />
          <ClaimBulkBar sel={bulk} dept={key ?? ''} onDone={() => setReloadTick((n) => n + 1)} />
          </BulkProvider>
        ) : key === 'claims' ? (
          <BulkProvider sel={bulk}>
            <ClaimsGenericSection rows={filtered as unknown as FlowRow[]} view={view === 'cards' ? 'cards' : 'lines'} onClient={setSel} onChanged={() => setReloadTick((n) => n + 1)} />
            {/* Acciones en lote sobre la cola de este departamento. */}
            <ClaimBulkBar sel={bulk} dept={key ?? ''} onDone={() => setReloadTick((n) => n + 1)} />
          </BulkProvider>
        ) : (
          // Todas las demás colas de departamento: misma selección múltiple que Claims.
          <BulkProvider sel={bulk}>
            <div style={view === 'cards'
              ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12, alignItems: 'start' }
              : { display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filtered.map((r) => <QueueItem key={r.presence_id} r={r} view={view === 'cards' ? 'cards' : 'lines'} onClient={setSel} neutral={neutral} deptKey={key} onChanged={() => setReloadTick((n) => n + 1)} />)}
            </div>
            <ClaimBulkBar sel={bulk} dept={key ?? ''} onDone={() => setReloadTick((n) => n + 1)} />
          </BulkProvider>
        )}
      </div>
      {sel && <ClaimDrawer claimId={sel} onClose={() => setSel(null)} />}
      {addLead && <AddLeadModal onClose={() => setAddLead(false)} onCreated={() => setReloadTick((n) => n + 1)} />}
    </div>
  );
}

/**
 * PESTAÑA "DONE" de Claim Review (pedido de Manuel 2026-08-11): cada decisión tomada —
 * QUIÉN la tomó, QUÉ decidió y CUÁNTO TARDÓ desde que el claim entró a la etapa.
 */
/** Mueve un día ISO hacia atrás o adelante sin pasar por UTC (que corre la fecha de noche). */
function diaMas(iso: string, paso: -1 | 1): string {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + paso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const hoyLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function ClaimReviewDone({ rows, dias, day, onDay, onClient }: {
  rows: CrDone[]; dias: Array<{ day: string; n: number }>;
  day: string | null; onDay: (d: string | null) => void;
  onClient: (id: string) => void;
}) {
  const { t } = useI18n();
  // Filtro por persona: sale de clickear su chip en el total. Vive acá y no arriba porque solo
  // tiene sentido en esta tabla.
  const [quien, setQuien] = useState<string | null>(null);
  const dur = (h: number | null): string => {
    if (h == null) return '—';
    if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`;
    if (h < 48) return `${h}h`;
    return `${Math.floor(h / 24)}d ${Math.round(h % 24)}h`;
  };
  const DEC: Record<string, { label: string; bg: string; fg: string }> = {
    accepted_new: { label: t('crv.dAcceptedNew'), bg: 'var(--green-soft)', fg: 'var(--green)' },
    accepted_existing: { label: t('crv.dAcceptedExisting'), bg: 'var(--accent-soft)', fg: 'var(--accent)' },
    denied: { label: t('crv.dDenied'), bg: 'var(--red-soft)', fg: 'var(--red)' },
  };
  /**
   * TOTAL POR PERSONA (pedido 2026-08-11: "un total sumando cuántos han hecho por usuario").
   *
   * Se cuenta sobre las filas que se están VIENDO, así que siempre cuadra con la tabla de abajo:
   * si elegís un día, es de ese día; si buscás un cliente, es de esa búsqueda. Un total traído
   * aparte podría no coincidir con lo que hay en pantalla, y dos números que no cuadran hacen que
   * no se le crea a ninguno.
   */
  const porPersona = (() => {
    const m = new Map<string, number>();
    for (const r of rows) {
      const k = r.by ?? t('crv.noUser');
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  })();
  const visibles = quien ? rows.filter((r) => (r.by ?? t('crv.noUser')) === quien) : rows;

  const totales = (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
      <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
        {/* El período se NOMBRA: "las últimas 300" no se puede escribir en una pantalla sin mentir. */}
        {day ? t('crv.totalsOn', { d: day }) : t('crv.totals90')}
      </span>
      {porPersona.map(([nombre, n]) => (
        <button key={nombre} className={`chipx${quien === nombre ? ' active' : ''}`}
          onClick={() => setQuien(quien === nombre ? null : nombre)}>
          {nombre} <span style={{ opacity: 0.7 }}>{n}</span>
        </button>
      ))}
      {porPersona.length > 1 && (
        <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
          · {t('crv.totalAll', { n: String(rows.length) })}
        </span>
      )}
    </div>
  );

  /* La barra de días. `dias` son los días que TIENEN decisiones (90 días): el desplegable evita
     retroceder de a un día a ver si hubo algo, y las flechas sirven para moverse al de al lado. */
  const barra = (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
      <button className={`chipx${day === null ? ' active' : ''}`} onClick={() => onDay(null)}>{t('crv.allDays')}</button>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <button className="btn ghost sm" title={t('crv.prevDay')}
          onClick={() => onDay(diaMas(day ?? hoyLocal(), -1))}><ChevronLeft size={13} strokeWidth={1.75} /></button>
        <input type="date" className="input" style={{ width: 150, fontSize: 'var(--fs-xs)' }}
          value={day ?? ''} max={hoyLocal()} onChange={(e) => onDay(e.target.value || null)} />
        <button className="btn ghost sm" title={t('crv.nextDay')} disabled={!day || day >= hoyLocal()}
          onClick={() => onDay(diaMas(day ?? hoyLocal(), 1))}><ChevronRight size={13} strokeWidth={1.75} /></button>
      </span>
      <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={() => onDay(hoyLocal())}>{t('att.today')}</button>
      {dias.length > 0 && (
        <select className="input" style={{ width: 200, fontSize: 'var(--fs-xs)' }}
          value={day ?? ''} onChange={(e) => onDay(e.target.value || null)}>
          <option value="">{t('crv.jumpDay')}</option>
          {dias.map((d) => <option key={d.day} value={d.day}>{d.day} · {d.n}</option>)}
        </select>
      )}
    </div>
  );
  if (!rows.length) {
    return (
      <div className="card" style={{ padding: 8 }}>
        {barra}
        {/* Con un día elegido el vacío significa "ese día no se decidió nada", que no es lo mismo
            que "no hay decisiones". Decirlo distinto evita pensar que la pantalla falló. */}
  <div className="page-empty">{day ? t('crv.doneEmptyDay', { d: day }) : t('crv.doneEmpty')}</div>
      </div>
    );
  }
  // El filtro por persona sobrevive al cambio de día a propósito (para seguir a alguien día por
  // día), así que hay que decir POR QUÉ la tabla quedó vacía: sin esto se ve una tabla en blanco
  // en un día que sí tuvo trabajo, y el filtro activo está tres líneas más arriba.
  if (!visibles.length) {
    return (
      <div className="card" style={{ padding: 8 }}>
        {barra}
        {totales}
        <div className="page-empty">
          {t('crv.noneBy', { n: quien ?? '' })}{' '}
          <button className="btn ghost sm" onClick={() => setQuien(null)}>{t('crv.clearWho')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: 8 }}>
      {barra}
      {totales}
      <table className="tbl">
        <thead>
          <tr><th>{t('crv.colClient')}</th><th>{t('crv.colDecision')}</th><th>{t('crv.colBy')}</th><th>{t('crv.colTook')}</th><th>{t('crv.colWhen')}</th></tr>
        </thead>
        <tbody>
          {visibles.map((r, i) => {
            const d = DEC[r.decision] ?? { label: r.decision, bg: 'var(--surface-2)', fg: 'var(--muted)' };
            return (
              <tr key={`${r.claim_id}-${i}`}>
                <td>
                  <button onClick={() => onClient(r.claim_id)}
                    style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer', fontWeight: 'var(--fw-semibold)', color: 'var(--ink)', textAlign: 'left' }}>
                    {r.homeowner_name || r.claim_number || `#${r.claim_id}`}
                  </button>
                  {r.claim_number && <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-mono)' }}>{r.claim_number}</div>}
                </td>
                <td><span className="badge" title={r.reason ?? ''} style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', background: d.bg, color: d.fg }}>{d.label}</span></td>
                <td style={{ fontSize: 'var(--fs-sm)' }}>{r.by ?? '—'}</td>
                {/* EL TIEMPO: el número grande es el TRABAJO (huecos cortos entre acciones), no el
                    reloj de pared. El reloj de pared sigue estando, en el tooltip: es el dato de
                    "cuánto esperó el claim", que también sirve, pero no es lo que se leía acá. */}
                <td style={{ fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-sm)' }}
                  title={r.hours != null ? t('crv.tookElapsed', { d: dur(r.hours) }) : ''}>
                  {dur(r.active_hours)}
                </td>
                <td className="muted" style={{ fontSize: 'var(--fs-xs)', whiteSpace: 'nowrap' }}>{new Date(r.decided_at).toLocaleString(localeUI(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
